/* Shared ship-date engine — resolved client-side so it never goes stale
   (Shopify caches storefront HTML, so a Liquid-rendered date would drift).

   ONE rule, ONE source of truth, four surfaces (cards, PDP, cart drawer, cart page):
     • Ships SAME DAY → "Ships Today — order by {6 PM ET in the visitor's local
       time}" when it's a business day AND before the 6 PM Eastern cutoff.
     • Otherwise the NEXT business day → "Ships Tomorrow" if that's the next
       calendar day, else the weekday name ("Ships Monday", "Ships Tuesday", …).
   Weekends AND US federal holidays are skipped (see HOLIDAYS).

   Surfaces:
     • Product cards   — .product-card__ship-row[data-ship-by]
     • PDP buy box     — .pdp-test-shipby[data-ship-by]
       (both get "Ships <label>" written into their [data-shipby-text] /
        .product-card__ship-text child)
     • Cart drawer / cart page — via window.BlackoutShip.shipLabel()

   ── MAINTENANCE ──────────────────────────────────────────────────────────────
   HOLIDAYS lists observed US federal holiday dates (the days the shop is closed
   and nothing processes). It currently covers 2026–2027. ADD THE NEXT YEAR each
   December so dates around holidays stay accurate. This is the ONLY place to
   update — cart drawer & cart page read it through window.BlackoutShip. */
(function () {
  'use strict';

  var CUTOFF_HOUR = 18; // 6 PM Eastern — orders placed after this lose "today"

  /* Observed US federal holidays as 'YYYY-MM-DD' in Eastern local terms. Observed
     means the weekday the shop is actually closed (e.g. Jul 4 2026 falls on a
     Saturday, observed Fri Jul 3). Conservative on purpose: skipping a holiday can
     only push the ship day LATER, never earlier. */
  var HOLIDAYS = {
    // 2026
    '2026-01-01': 1, // New Year's Day
    '2026-01-19': 1, // MLK Jr. Day
    '2026-02-16': 1, // Presidents' Day
    '2026-05-25': 1, // Memorial Day
    '2026-06-19': 1, // Juneteenth
    '2026-07-03': 1, // Independence Day (observed, Jul 4 = Sat)
    '2026-09-07': 1, // Labor Day
    '2026-10-12': 1, // Columbus Day
    '2026-11-11': 1, // Veterans Day
    '2026-11-26': 1, // Thanksgiving
    '2026-12-25': 1, // Christmas Day
    // 2027
    '2027-01-01': 1, // New Year's Day
    '2027-01-18': 1, // MLK Jr. Day
    '2027-02-15': 1, // Presidents' Day
    '2027-05-31': 1, // Memorial Day
    '2027-06-18': 1, // Juneteenth (observed, Jun 19 = Sat)
    '2027-07-05': 1, // Independence Day (observed, Jul 4 = Sun)
    '2027-09-06': 1, // Labor Day
    '2027-10-11': 1, // Columbus Day
    '2027-11-11': 1, // Veterans Day
    '2027-11-25': 1, // Thanksgiving
    '2027-12-24': 1, // Christmas Day (observed, Dec 25 = Sat)
    '2027-12-31': 1  // New Year's Day 2028 (observed, Jan 1 = Sat)
  };

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function key(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }

  // "Now" as a Date whose local fields equal the Eastern (America/New_York) wall
  // clock — so getDay()/getHours()/getDate() read in Eastern. Handles EST/EDT.
  function etNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  }
  function isBusinessDay(d) {
    var w = d.getDay();
    if (w === 0 || w === 6) return false;       // weekend
    if (HOLIDAYS[key(d)]) return false;          // federal holiday
    return true;
  }
  function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
  }

  /* Soonest day an order placed now would ship: today if it's a business day
     before the 6 PM ET cutoff, otherwise the next business day (weekends +
     holidays skipped). */
  function soonestShipDay() {
    var d = etNow();
    if (isBusinessDay(d) && d.getHours() < CUTOFF_HOUR) return d;
    do { d.setDate(d.getDate() + 1); } while (!isBusinessDay(d));
    return d;
  }

  /* "Today" | "Tomorrow" | weekday name ("Monday" …) for the soonest ship day. */
  function shipLabel() {
    var now = etNow();
    var ship = soonestShipDay();
    if (sameDay(ship, now)) return 'Today';
    var tom = new Date(now); tom.setDate(tom.getDate() + 1);
    if (sameDay(ship, tom)) return 'Tomorrow';
    return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(ship);
  }

  /* The 6 PM ET cutoff, expressed in the VISITOR's local timezone (so a Pacific
     visitor sees "3:00 PM PDT", an Eastern one "6:00 PM EDT"). We build the exact
     UTC instant of today's CUTOFF_HOUR:00 in America/New_York, then format it in
     the runtime's local zone. tzOffsetMs handles EST/EDT automatically. */
  function tzOffsetMs(tz, date) {
    var utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    var zoned = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return zoned.getTime() - utc.getTime();
  }
  function orderByLocal() {
    try {
      var et = etNow();
      var guess = Date.UTC(et.getFullYear(), et.getMonth(), et.getDate(), CUTOFF_HOUR, 0, 0);
      var instant = new Date(guess - tzOffsetMs('America/New_York', new Date(guess)));
      return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(instant);
    } catch (e) { return '6:00 PM ET'; }
  }

  /* Full display string: "Ships Today — order by 3:00 PM PDT" on a same-day
     business day, otherwise just "Ships Tomorrow" / "Ships Monday" / … . The
     order-by nudge only appears while ordering today still beats the cutoff. */
  function shipText() {
    var label = shipLabel();
    var t = 'Ships ' + label;
    if (label === 'Today') t += ' — order by ' + orderByLocal();
    return t;
  }

  // Single source of truth for the cart drawer & cart page inline scripts.
  window.BlackoutShip = {
    etNow: etNow,
    isBusinessDay: isBusinessDay,
    soonestShipDay: soonestShipDay,
    shipLabel: shipLabel,
    orderByLocal: orderByLocal,
    shipText: shipText,
    CUTOFF_HOUR: CUTOFF_HOUR
  };

  /* ── DOM fill for cards + PDP ── */
  function textTarget(el) {
    return el.querySelector('[data-shipby-text]') ||
           el.querySelector('.product-card__ship-text') || el;
  }

  function fill() {
    var text;
    try { text = shipText(); }
    catch (e) { return; } // leave the Liquid no-JS fallback in place
    var els = document.querySelectorAll('[data-ship-by]:not([data-shipby-done])');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var label = textTarget(el);
      if (label) label.textContent = text;
      el.setAttribute('data-shipby-done', '1');
    }
  }
  // Re-runnable for dynamically inserted cards/PDPs (filters, quick-add, swaps).
  window.renderCardShipBy = fill;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fill);
  } else {
    fill();
  }

  // Cards & the PDP get re-rendered by Shopify's Section Rendering (filters,
  // pagination, variant/product swaps) and quick-add. A debounced observer
  // re-fills only un-done elements.
  if (window.MutationObserver) {
    var queued = false;
    var obs = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || window.setTimeout)(function () { queued = false; fill(); });
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
